import React from 'react';
import { Link } from 'react-router-dom';
import { Milestone } from '../types/milestone';
import Card from './Card';
import Badge from './Badge';
import Button from './Button';

interface MilestoneCardProps {
  milestone: Milestone;
  projectName?: string;
  taskCount?: number;
  onEdit?: () => void;
  onDelete?: () => void;
  onViewTasks?: () => void;
}

const MilestoneCard: React.FC<MilestoneCardProps> = ({
  milestone,
  projectName,
  taskCount,
  onEdit,
  onDelete,
  onViewTasks,
}) => {
  // Format target date
  const targetDate = new Date(milestone.target_date);
  const formattedDate = targetDate.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  // Format status label
  const formatStatus = (status: string): string => {
    return status.replace('_', ' ').replace(/\b\w/g, (char) => char.toUpperCase());
  };

  // Determine progress bar color
  const getProgressColor = (): string => {
    const completion = milestone.completion_percentage ?? 0;
    if (completion >= 100) return 'bg-green-500';
    if (completion >= 50) return 'bg-blue-500';
    return 'bg-gray-400';
  };

  return (
    <Card variant="glass" className="hover:-translate-y-1 transition-all duration-300">
      <Card.Header>
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-1">
              <Link
                to={`/milestones/${milestone.id}`}
                className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
              >
                {milestone.name}
              </Link>
            </h3>
            {projectName && (
              <p className="text-sm text-gray-600 dark:text-gray-400">Project: {projectName}</p>
            )}
          </div>
          <div className="ml-4 flex flex-wrap gap-2">
            {onViewTasks && (
              <Button
                variant="outline"
                size="sm"
                onClick={onViewTasks}
                title={`View all tasks ${taskCount !== undefined ? `(${taskCount})` : ''}`}
              >
                View Tasks{taskCount !== undefined ? ` (${taskCount})` : ''}
              </Button>
            )}
            {onEdit && (
              <Button variant="primary" size="sm" onClick={onEdit}>
                Edit
              </Button>
            )}
            {onDelete && (
              <Button variant="danger" size="sm" onClick={onDelete}>
                Delete
              </Button>
            )}
          </div>
        </div>
      </Card.Header>

      <Card.Body>
        {/* Description */}
        {milestone.description && (
          <p className="text-gray-900 dark:text-gray-100 mb-4">{milestone.description}</p>
        )}

        {/* Target Date */}
        <div className="mb-4">
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Target Date: </span>
          <span className="text-sm text-gray-900 dark:text-gray-100">{formattedDate}</span>
        </div>

        {/* Status Badge */}
        <div className="mb-4">
          <Badge 
            status={(milestone.status || 'upcoming') as any} 
            size="md"
          >
            {formatStatus(milestone.status ?? 'upcoming')}
          </Badge>
        </div>

        {/* Completion Progress */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Progress</span>
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {(milestone.completion_percentage ?? 0).toFixed(0)}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
            <div
              className={`h-full transition-all duration-300 ${getProgressColor()}`}
              style={{ width: `${Math.min(milestone.completion_percentage ?? 0, 100)}%` }}
              role="progressbar"
              aria-valuenow={milestone.completion_percentage ?? 0}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </div>
        </div>
      </Card.Body>

      <Card.Footer>
        <div className="text-xs text-gray-600 dark:text-gray-400">
          Created {new Date(milestone.created_at).toLocaleDateString()}
        </div>
      </Card.Footer>
    </Card>
  );
};

export default MilestoneCard;
